/**
 * BalanceEntryForm Component
 * Form for adding a new balance entry to an account
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { accountsService } from '@/services/accounts';
import { ApiClientError } from '@/services/api';
import { accountKeys } from '@/hooks/useAccounts';

export interface BalanceEntryFormProps {
  accountId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
}

interface FormData {
  balance: string;
  availableBalance: string;
  effectiveDate: string;
  notes: string;
}

/**
 * Get today's date in local timezone as YYYY-MM-DD string
 */
function getLocalDateString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const getDefaultFormData = (): FormData => ({
  balance: '',
  availableBalance: '',
  effectiveDate: getLocalDateString(),
  notes: '',
});

export default function BalanceEntryForm({
  accountId,
  onSuccess,
  onCancel,
  className,
}: BalanceEntryFormProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<FormData>(getDefaultFormData());
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (data: Parameters<typeof accountsService.addBalanceEntry>[1]) =>
      accountsService.addBalanceEntry(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      queryClient.invalidateQueries({ queryKey: ['balance-history', accountId] });
      setFormData(getDefaultFormData());
      setError(null);
      onSuccess?.();
    },
    onError: (err) => {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to add balance entry. Please try again.');
      }
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate balance
    const balance = parseFloat(formData.balance);
    if (isNaN(balance)) {
      setError('Please enter a valid balance amount');
      return;
    }

    // Validate date is not in the future
    const effectiveDate = new Date(formData.effectiveDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (effectiveDate > today) {
      setError('Effective date cannot be in the future');
      return;
    }

    // Parse available balance if provided
    const availableBalance = formData.availableBalance.trim()
      ? parseFloat(formData.availableBalance)
      : undefined;

    if (availableBalance !== undefined && isNaN(availableBalance)) {
      setError('Please enter a valid available balance amount');
      return;
    }

    mutation.mutate({
      balance,
      availableBalance,
      effectiveDate: formData.effectiveDate,
      source: 'manual',
      notes: formData.notes.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className={className}>
      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Balance"
            type="number"
            step="0.01"
            value={formData.balance}
            onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
            placeholder="0.00"
            autoFocus
          />
          <Input
            label="Available Balance (optional)"
            type="number"
            step="0.01"
            value={formData.availableBalance}
            onChange={(e) => setFormData({ ...formData, availableBalance: e.target.value })}
            placeholder="0.00"
            hint="For checking accounts, etc."
          />
        </div>

        <Input
          label="Effective Date"
          type="date"
          value={formData.effectiveDate}
          onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
          max={getLocalDateString()}
        />

        <Input
          label="Notes (optional)"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="e.g., Annual property assessment"
        />

        <div className="flex gap-3 pt-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={mutation.isPending}
              className="flex-1"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            isLoading={mutation.isPending}
            className={onCancel ? 'flex-1' : 'w-full'}
          >
            Add Balance Entry
          </Button>
        </div>
      </div>
    </form>
  );
}
